import { useState, useRef, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, Lock, Landmark, Building2, Package, User, Mail, Home } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useLanguage } from '../contexts/LanguageContext'
import { trackMetaInitiateCheckout, trackMetaPurchase } from '../utils/analytics'

const Checkout = () => {
  const navigate = useNavigate()
  const { items, getTotalPrice, clearCart } = useCart()
  const { language } = useLanguage()
  const [paymentMethod, setPaymentMethod] = useState<'bankTransfer'>('bankTransfer')
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    apartment: '',
    postalCode: '',
    city: '',
    phone: '',
    country: 'Türkiye',
    taxId: '',
    saveInfo: false,
    billingAddressSame: true
  })

  const [copiedField, setCopiedField] = useState<'account' | 'iban' | null>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  const handleCopy = (value: string, field: 'account' | 'iban') => {
    const copyValue = async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = value
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }

        setCopiedField(field)
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = window.setTimeout(() => setCopiedField(null), 2000)
      } catch (error) {
        console.error('Copy failed', error)
      }
    }

    copyValue()
  }

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  // Track InitiateCheckout when user lands on checkout page
  useEffect(() => {
    if (items.length > 0) {
      trackMetaInitiateCheckout({
        content_ids: items.map(item => item.id),
        content_category: 'consulting',
        num_items: items.reduce((acc, item) => acc + item.quantity, 0),
        value: total,
      })
    }
  }, [])

  const total = getTotalPrice()
  const vatRate = 0.20
  const vatAmount = total * vatRate / (1 + vatRate)
  const subtotal = total - vatAmount

  const translations = {
    tr: {
      pageTitle: 'Ödeme',
      contact: 'İletişim Bilgileri',
      email: 'E-posta',
      delivery: 'Teslimat Bilgileri',
      country: 'Ülke',
      firstName: 'Ad',
      lastName: 'Soyad',
      company: 'Şirket (isteğe bağlı)',
      address: 'Adres',
      apartment: 'Apartman, daire vb. (isteğe bağlı)',
      postalCode: 'Posta kodu (isteğe bağlı)',
      city: 'Şehir',
      phone: 'Telefon',
      saveInfo: 'Bir sonraki işlem için bu bilgileri kaydet',
      paymentMethod: 'Ödeme Yöntemi',
      securePayment: 'Tüm işlemler güvenli ve şifrelidir',
      bankTransfer: 'Havale / EFT ile Ödeme',
      bankTransferDescription: 'Ödemeyi Thinklyze banka hesabına havale veya EFT ile gerçekleştirebilirsiniz.',
      bankTransferInstructions: 'Aşağıdaki banka bilgilerini kullanarak havale/EFT yapabilirsiniz. Açıklama kısmına sipariş numaranızı eklemeyi unutmayın.',
      bankDetails: 'Banka: Garanti BBVA\nŞube: Şirinevler\nHesap Sahibi: THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ\nIBAN: TR11 0006 2000 1870 0006 2909 11',
      bankName: 'Banka: Garanti BBVA',
      branchName: 'Şube: Şirinevler',
      accountHolderLabel: 'Hesap Sahibi',
      accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
      ibanLabel: 'IBAN',
      iban: 'TR11 0006 2000 1870 0006 2909 11',
      copyAccountHolder: 'Hesap sahibini kopyala',
      copyIban: 'IBAN\'ı kopyala',
      copiedAccount: 'Hesap sahibi kopyalandı!',
      copiedIban: 'IBAN kopyalandı!',
      billingAddress: 'Fatura Adresi',
      sameAsShipping: 'Teslimat adresiyle aynı',
      differentAddress: 'Farklı bir fatura adresi kullan',
      additionalInfo: 'Ek Bilgiler',
      taxId: 'TC Kimlik No / Vergi No (isteğe bağlı)',
      submitOrder: 'Siparişi Tamamla',
      orderSuccess: 'Siparişiniz alındı! Sipariş numaranız:',
      orderFollowUp: 'Ödemeniz ulaştığında danışmanımız sizinle iletişime geçecektir.',
      orderSummary: 'Sipariş Özeti',
      subtotal: 'Ara Toplam',
      vat: 'KDV',
      total: 'Toplam',
      sessions: 'seans',
      hour: 'saat',
      emptyCart: 'Sepetiniz boş',
      returnToConsulting: 'Danışmanlık sayfasına dön'
    },
    en: {
      pageTitle: 'Checkout',
      contact: 'Contact Information',
      email: 'Email',
      delivery: 'Delivery Information',
      country: 'Country',
      firstName: 'First Name',
      lastName: 'Last Name',
      company: 'Company (optional)',
      address: 'Address',
      apartment: 'Apartment, suite, etc. (optional)',
      postalCode: 'Postal code (optional)',
      city: 'City',
      phone: 'Phone',
      saveInfo: 'Save this information for next time',
      paymentMethod: 'Payment Method',
      securePayment: 'All transactions are secure and encrypted',
      bankTransfer: 'Bank Transfer',
      bankTransferDescription: 'You can complete your payment via bank transfer/EFT to Thinklyze bank account.',
      bankTransferInstructions: 'Use the bank details below to complete your transfer. Please include your order number in the payment reference.',
      bankDetails: 'Bank: Garanti BBVA\nBranch: Şirinevler\nAccount Holder: THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ\nIBAN: TR11 0006 2000 1870 0006 2909 11',
      bankName: 'Bank: Garanti BBVA',
      branchName: 'Branch: Şirinevler',
      accountHolderLabel: 'Account Holder',
      accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
      ibanLabel: 'IBAN',
      iban: 'TR11 0006 2000 1870 0006 2909 11',
      copyAccountHolder: 'Copy account holder',
      copyIban: 'Copy IBAN',
      copiedAccount: 'Account holder copied!',
      copiedIban: 'IBAN copied!',
      billingAddress: 'Billing Address',
      sameAsShipping: 'Same as delivery address',
      differentAddress: 'Use a different billing address',
      additionalInfo: 'Additional Information',
      taxId: 'ID Number / Tax Number (optional)',
      submitOrder: 'Place Order',
      orderSuccess: 'Your order has been received! Your order number:',
      orderFollowUp: 'We will contact you once your payment is received.',
      orderSummary: 'Order Summary',
      subtotal: 'Subtotal',
      vat: 'VAT',
      total: 'Total',
      sessions: 'sessions',
      hour: 'hour',
      emptyCart: 'Your cart is empty',
      returnToConsulting: 'Return to consulting page'
    },
    ru: {
      pageTitle: 'Оформление заказа',
      contact: 'Контактная информация',
      email: 'Email',
      delivery: 'Информация о доставке',
      country: 'Страна',
      firstName: 'Имя',
      lastName: 'Фамилия',
      company: 'Компания (необязательно)',
      address: 'Адрес',
      apartment: 'Квартира, офис и т.д. (необязательно)',
      postalCode: 'Почтовый индекс (необязательно)',
      city: 'Город',
      phone: 'Телефон',
      saveInfo: 'Сохранить эту информацию для следующего раза',
      paymentMethod: 'Способ оплаты',
      securePayment: 'Все транзакции безопасны и зашифрованы',
      bankTransfer: 'Банковский перевод',
      bankTransferDescription: 'Вы можете оплатить заказ банковским переводом на счет Thinklyze.',
      bankTransferInstructions: 'Используйте банковские реквизиты ниже и укажите номер заказа в назначении платежа.',
      bankDetails: 'Банк: Garanti BBVA\nОтделение: Шириневлер\nПолучатель: THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ\nIBAN: TR11 0006 2000 1870 0006 2909 11',
      bankName: 'Банк: Garanti BBVA',
      branchName: 'Отделение: Шириневлер',
      accountHolderLabel: 'Получатель',
      accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
      ibanLabel: 'IBAN',
      iban: 'TR11 0006 2000 1870 0006 2909 11',
      copyAccountHolder: 'Скопировать получателя',
      copyIban: 'Скопировать IBAN',
      copiedAccount: 'Получатель скопирован!',
      copiedIban: 'IBAN скопирован!',
      billingAddress: 'Адрес выставления счета',
      sameAsShipping: 'Совпадает с адресом доставки',
      differentAddress: 'Использовать другой адрес для выставления счета',
      additionalInfo: 'Дополнительная информация',
      taxId: 'ИНН / Налоговый номер (необязательно)',
      submitOrder: 'Завершить заказ',
      orderSuccess: 'Ваш заказ получен! Номер заказа:',
      orderFollowUp: 'Мы свяжемся с вами после получения платежа.',
      orderSummary: 'Сводка заказа',
      subtotal: 'Промежуточный итог',
      vat: 'НДС',
      total: 'Итого',
      sessions: 'сеансов',
      hour: 'час',
      emptyCart: 'Ваша корзина пуста',
      returnToConsulting: 'Вернуться на страницу консультаций'
    }
  }

  const t = translations[language as 'tr' | 'en' | 'ru']

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address || !formData.city || !formData.phone) {
      alert(language === 'tr' ? 'Lütfen tüm zorunlu alanları doldurun' : 'Please fill in all required fields')
      return
    }

    // Create order data for payment gateway
    const orderNumber = `THK-${Date.now()}`
    const orderData = {
      items,
      total,
      customer: formData,
      paymentMethod,
      orderNumber
    }

    // Store order data in localStorage (will be used after payment)
    localStorage.setItem('pendingOrder', JSON.stringify(orderData))

    const supportNumber = '905536576454'
    const orderLines = items.map((item, index) => {
      const itemName =
        language === 'tr'
          ? item.name
          : language === 'en'
          ? item.nameEn
          : (item as any).nameRu || item.nameEn || item.name
      return `${index + 1}) ${itemName} | ${item.quantity} x ${item.totalPrice.toLocaleString('tr-TR')}₺ = ${(item.totalPrice * item.quantity).toLocaleString('tr-TR')}₺`
    })

    const messageLines = [
      'Yeni Havale/EFT Sipariş Talebi',
      `Sipariş No: ${orderNumber}`,
      `Toplam Tutar: ${total.toLocaleString('tr-TR')}₺`,
      '',
      'Müşteri Bilgileri',
      `Ad Soyad: ${formData.firstName} ${formData.lastName}`,
      `E-posta: ${formData.email}`,
      `Telefon: ${formData.phone}`,
      `Şirket: ${formData.company || '-'}`,
      '',
      'Teslimat Adresi',
      `Adres: ${formData.address}`,
      `Adres Detayı: ${formData.apartment || '-'}`,
      `Şehir: ${formData.city}`,
      `Posta Kodu: ${formData.postalCode || '-'}`,
      `Ülke: ${formData.country}`,
      '',
      formData.billingAddressSame
        ? 'Fatura Adresi: Teslimat ile aynı'
        : `Fatura Adresi: ${formData.address}`,
      formData.taxId ? `Vergi/T.C. No: ${formData.taxId}` : 'Vergi/T.C. No: -',
      '',
      'Sipariş Detayları',
      ...orderLines,
      '',
      'Ödeme Yöntemi: Havale / EFT',
      'Lütfen havale açıklamasında sipariş numarasını belirtiniz.',
    ]

    const whatsappUrl = `https://wa.me/${supportNumber}?text=${encodeURIComponent(messageLines.join('\n'))}`
    window.open(whatsappUrl, '_blank')

    // Track Meta Pixel Purchase event
    trackMetaPurchase({
      content_ids: items.map(item => item.id),
      content_name: items.map(item => language === 'tr' ? item.name : item.nameEn).join(', '),
      content_type: 'consulting',
      value: total,
      num_items: items.reduce((acc, item) => acc + item.quantity, 0),
    })

    clearCart()
    navigate(`/consulting/success?order=${encodeURIComponent(orderNumber)}`)
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#f9f9f9] pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.emptyCart}</h2>
            <p className="text-gray-600 mb-6">
              {language === 'tr' 
                ? 'Ödeme yapmak için sepetinizde ürün olması gerekiyor.'
                : 'You need items in your cart to proceed with checkout.'}
            </p>
            <button
              onClick={() => navigate('/consulting')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t.returnToConsulting}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{t.pageTitle} - Thinklyze</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#f9f9f9] pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Form */}
            <div className="space-y-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      {t.contact}
                    </h2>
                  </div>
                  <input
                    type="email"
                    placeholder={t.email}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Delivery Information */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600" />
                    {t.delivery}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <select
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>Türkiye</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder={t.firstName}
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <input
                        type="text"
                        placeholder={t.lastName}
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <input
                      type="text"
                      placeholder={t.company}
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      placeholder={t.address}
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <input
                      type="text"
                      placeholder={t.apartment}
                      value={formData.apartment}
                      onChange={(e) => setFormData({...formData, apartment: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder={t.postalCode}
                        value={formData.postalCode}
                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder={t.city}
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <input
                      type="tel"
                      placeholder={t.phone}
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.saveInfo}
                        onChange={(e) => setFormData({...formData, saveInfo: e.target.checked})}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{t.saveInfo}</span>
                    </label>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-blue-600" />
                    {t.paymentMethod}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
                    <Lock className="w-4 h-4" />
                    {t.securePayment}
                  </div>
                  
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-4 border-2 border-blue-500 rounded-xl bg-blue-50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="bankTransfer"
                        checked={paymentMethod === 'bankTransfer'}
                        onChange={() => setPaymentMethod('bankTransfer')}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">{t.bankTransfer}</div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {t.bankTransferDescription}
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                    <p className="text-sm text-gray-700 mb-3">{t.bankTransferInstructions}</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                      <p className="text-sm text-gray-800 font-mono whitespace-pre-line">
                        {`${t.bankName}\n${t.branchName}`}
                      </p>
                      <div className="mt-4 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <p className="text-sm text-gray-800 font-mono">
                            {`${t.accountHolderLabel}: ${t.accountHolder}`}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleCopy(t.accountHolder, 'account')}
                            className="self-start sm:self-auto inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {t.copyAccountHolder}
                          </button>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <p className="text-sm text-gray-800 font-mono">
                            {`${t.ibanLabel}: ${t.iban}`}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleCopy(t.iban, 'iban')}
                            className="self-start sm:self-auto inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {t.copyIban}
                          </button>
                        </div>
                        {copiedField === 'account' && (
                          <span className="text-xs text-green-600">{t.copiedAccount}</span>
                        )}
                        {copiedField === 'iban' && (
                          <span className="text-xs text-green-600">{t.copiedIban}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    {t.billingAddress}
                  </h2>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.billingAddressSame}
                        onChange={() => setFormData({...formData, billingAddressSame: true})}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t.sameAsShipping}</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.billingAddressSame}
                        onChange={() => setFormData({...formData, billingAddressSame: false})}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">{t.differentAddress}</span>
                    </label>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    {t.additionalInfo}
                  </h2>
                  <input
                    type="text"
                    placeholder={t.taxId}
                    value={formData.taxId}
                    onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold text-lg shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                >
                  <Lock className="w-5 h-5" />
                  {t.submitOrder}
                </button>
              </form>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">{t.orderSummary}</h2>
                
                {/* Items */}
                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                        <Package className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1">
                          {language === 'tr' ? item.name : item.nameEn}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {item.quantity} × {item.hours} {t.hour}
                        </p>
                        <p className="text-sm font-bold text-gray-900 mt-1">
                          {(item.totalPrice * item.quantity).toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Price Summary */}
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t.subtotal}</span>
                    <span className="font-semibold text-gray-900">
                      {subtotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t.vat} (%20)</span>
                    <span className="font-semibold text-gray-900">
                      {vatAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-lg font-bold text-gray-900">{t.total}</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {total.toLocaleString('tr-TR')} ₺
                    </span>
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{t.securePayment}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Checkout















